import { WithdrawalCurrency } from '../constants/servicing';
import { UploadInvoiceErrorDetails } from '../viewsv2/FinanceRequestDetail/UploadInvoiceStep';
import { APIAsset } from './application';
import { ApprovedBankAccount } from './bankAccount';
import { APITracking } from './tracking';
import { Nullable } from './utils';

export type APIMoneyField = {
  amount: number;
  currency: string;
};

export type CreditLineMetrics = {
  availableCredit: Nullable<number>;
  creditLineSize: Nullable<number>;
  netFundsEmployed: Nullable<number>;
  cashReserves: Nullable<number>;
  reserveEscrow: Nullable<number>;
};

export type CreditFacility = {
  facilityId: number;
  facilityType: string;
  status: 'active' | 'inactive' | 'unknown';
};

export type BuyerInformation = {
  clientCompanyUuid: string;
  buyerCompanyUuid: string;
  buyerName: string;
  creditLine: {
    facilityType: string;
    creditSublimit: APIMoneyField;
    noBuy: 'true' | 'false';
    netFundsEmployeed: APIMoneyField;
    availableCredit: APIMoneyField;
  };
  nonFactoredBalance: APIMoneyField;
  insurance: {
    insurerName: string;
    insurerAmount: APIMoneyField;
    noaStatus: 'signed' | 'pending' | 'sent' | 'waived';
    noaRequired: 'no' | 'yes';
  };
};

export type SkipLimitPagination = {
  skip: number;
  limit: number;
  total: number;
};

export type PagePagination = {
  page: number;
  pageSize: number;
  total: number;
};

export enum SortingOrder {
  ASC = 'asc',
  DESC = 'desc',
}
export type Sorting = {
  sortBy?: string;
  sortOrder?: SortingOrder;
};

export type BuyerDetails = {
  address: string;
  buyerCompanyUuid: string;
  buyerName: string;
  clientCompanyUuid: string;
  country: string;
  currency: string;
  emailAddress: Nullable<string>;
  noaSentDate: Nullable<string>;
  noaReceivedDate: Nullable<string>;
  noaWaivedDate: Nullable<string>;
  status: string;
  taxId: Nullable<string>;
};

export type AvailableCreditLine = {
  clientCompanyUuid: string;
  facilityId: number;
  facilityType: string;
  createdDate: string;
  status: 'active' | 'inactive';
  advanceRate: number;
  netFundsEmployed: APIMoneyField;
  availableCredit: APIMoneyField;
  creditLineSize: APIMoneyField;
};

export type BankAccount = {
  clientCompanyUuid: string;
  clientBankKey: number;
  bankName: string;
  bankAccountNo: string;
  bankAbaRoutingNo: string;
  bankSwiftCode: string;
  bankCurrency: string;
  bankStatus: string;
  bank: {
    address: string;
    city: string;
    zipCode: string;
    countryCode: string;
    phone: string;
    contact: string;
  };
  interBank: {
    name: string;
    accountNo: string;
    abaRoutingNo: string;
    swiftCode: string;
  };
};

export type WithdrawMoneyBank = {
  bankAccountNumber: string;
  bankName: string;
  bankCurrency: string;
};

export type WithdrawMoney = WithdrawMoneyBank & {
  fxNeeded: boolean;
  fxRate: Nullable<number>;
  currency: Nullable<WithdrawalCurrency>;
  amount: number;
  available: number;
  convertedFxAmount: Nullable<number>;
};

export type TimelineInvoiceStatus = Exclude<InvoiceStatus, 'held' | 'defaulted' | 'error'>;

export type InvoiceStatus = 'open' | 'closed' | 'overdue' | 'pending' | 'held' | 'defaulted' | 'error';

export type Invoice = {
  buyerId: string;
  invoiceKey: number;
  invoiceNumber: string;
  buyerName: string;
  buyerCountry: string;
  status: InvoiceStatus;
  invoiceAmount: APIMoneyField;
  invoiceDate: string;
  dueDate: string;
  purchasedDate: Nullable<string>;
  advanceDate: Nullable<string>;
  closureDate: Nullable<string>;
  advanceAmount: APIMoneyField;
  cbkAmount: APIMoneyField;
  discount: APIMoneyField;
  receivedAmount: APIMoneyField;
  balance: APIMoneyField;
  transactionId: number;
  id: number;
  factNf: 'factored' | 'inReview' | 'nonFactored';
  batchNumber: number;
  reserveEscrow: APIMoneyField;
  trackingInformation?: APITracking[];
  description: Nullable<string>;
  poNumber: Nullable<string>;
};

export type APIGetInvoiceAggregates = {
  clientCompanyUuid: string;
  totalInvoicesCount: number;
  factoredInvoicesCount: number;
  inReviewInvoicesCount: number;
  openInvoicesCount: number;
  overdueInvoicesCount: number;
  closedInvoicesCount: number;
  defaultedInvoicesCount: number;
  totalOverdue: APIMoneyField;
  totalPending: APIMoneyField;
  totalInReview: APIMoneyField;
  totalInvoices: APIMoneyField;
  totalOutstanding: APIMoneyField;
};

export type APITransfer = {
  transactionId: number;
  clientCompanyUuid: string;
  batchNumber: number;
  postedDate: string;
  transferCategory: string; //type it
  transferStatus: string; //type it
  paymentType: string; //type it
  invoiceCount: number;
  wireAmount: number;
  inputAmount: number;
  arAmount: number;
  advanceRate: number;
  reserveEscrow: number;
  expensesDeducted: number;
  advanceAmount: number;
  reservesEarned: number;
  reservesHeld: number;
  payeeBankId: number;
  payeeBankAccountNumber: string;
  payeeBankName: string;
  payeeBankAbaNumber: string;
  payeeBankSwiftCode: Nullable<string>;
  payeeBankCurrency: Nullable<string>;
};

export type APITransferDetail = {
  clientCompanyUuid: number;
  clientName: string;
  batchNumber: number;
  transactionId: number;
  postedDate: string;
  invoiceKey: number;
  invoiceNumber: string;
  clientRefCode: string;
  buyerName: string;
  invoiceDate: string;
  dueDate: string;
  purchaseDate: string;
  invoiceAmount: number;
  advanceRate: number;
  advanceAmount: number;
  reserveEscrow: number;
  financingStatus: string;
};

export type APIGetIncreaseBuyerSublimitRequests = APIIncreaseBuyerSublimitRequest[];

export type APIIncreaseBuyerSublimitRequest = {
  buyerId: string;
  buyerName: string;
  comments: string;
  company: number;
  createdAt: string;
  deletedAt: Nullable<string>;
  id: number;
  increaseFacility: boolean;
  paymentTerms: string;
  requestedCreditSubLimit: number;
  updatedAt: string;
  user: number;
};

export type APIDiscount = {
  clientCompanyUuid: string;
  buyerCompanyUuid: string;
  invoiceId: number;
  invoiceNumber: string;
  accruingDays: number;
  discount: APIMoneyField;
  additionalFee: APIMoneyField;
  extraFee: APIMoneyField;
  bumpFee: APIMoneyField;
  dailyFee: APIMoneyField;
  transactionId: Nullable<string>;
};

export type APIGetDiscounts = {
  discounts: APIDiscount[];
};

export type APIGetMarcoWrapped = {
  clientCountry: string;
  countFinancedInvoices: number;
  totalAmountFactoredInvAmt: number;
  totalAmountFactoredAdvAmt: number;
  percentageOfCollectedOnTimeInv: number;
  countOfFactoredBuyers: number;
  maxTotalInvAmtFactByBuyer: number;
  countOfFundingReq: number;
  minInvoicePurchasedDate: string;
};

export type ReportParams = {
  [key: string]: string;
};

export type APIContact = {
  contactId: string;
  name: string;
  email: string;
  websiteAccess: boolean;
  authorizedRequestFundings: boolean;
};

export type APICompanyContacts = {
  clientCompanyUuid: string;
  contacts: APIContact[];
};

export type APIPayment = {
  clientName: string;
  clientUuid: string;
  buyerName: string;
  buyerKey: number;
  buyerId: string;
  clientRefCode: string;
  postedDate: string;
  transactionId: number;
  batchNumber: number;
  checkId: number;
  checkNumber: string;
  reference: Nullable<string>;
  status: 'completed' | 'pending' | 'unknown';
  amount: number;
  appliedToAr: number;
  appliedToAdvance: number;
  reserveEscrow: number;
  discountFees: number;
  appliedToCashReserves: number;
  invoices: number;
  category: 'collection' | 'adjustment';
};

export type APIGetPaymentDetails = {
  pagination: SkipLimitPagination;
  paymentDetails: APIPaymentDetails;
};

export type APIPaymentDetails = {
  transactionDetails: {
    clientUuid: string;
    clientName: string;
    buyerName: string;
    buyerKey: number;
    buyerId: string;
    clientRefCode: string;
    transactionId: number;
    postedDate: string;
  };
  invoiceDetails: APIPaymentInvoice[];
};

export type APIPaymentInvoice = {
  batchNumber: number;
  checkId: number;
  checkNumber: string;
  paymentId: number;
  invoiceId: number;
  invoiceNumber: string;
  invoiceAmount: number;
  invoiceBalance: number;
  checkAmount: number;
  appliedToAr: number;
  appliedToAdvanced: number;
  appliedToFee: number;
  reserveAmount: number;
  activityType: 'non_factored' | 'chargeback' | 'over_payment' | 'unapplied_cash' | 'payment';
};

export type APIWithdrawalRequest = {
  id: number;
  createdAt: string;
  updatedAt: string;
  deletedAt: Nullable<string>;
  amount: number;
  available: number;
  fxNeeded: boolean;
  fxRate: Nullable<number>;
  currency: Nullable<string>;
  convertedFxAmount: Nullable<number>;
  bankAccountNumber: string;
  bankName: string;
  bankCurrency: string;
  company: number;
  user: number;
};

export type APIFinanceRequestAggregations = {
  draft: number;
  processing: number;
  needsAttention: number;
  completed: number;
};

export type APIFinanceRequestReviewStatistics = {
  facilityDetails: {
    expectedAdvanceAmount: number;
    currentFacilityAvailability: number;
    resultingFacilityAvailability: number;
  };
  invoicesDetails: {
    expectedReserves: number;
    expensesDeducted: number;
    totalInvoiceAmount: number;
    expectedReservesEscrow: number;
    expectedTransferAmount: number;
  };
  reviewAnalyticsUuid: string;
  buyersResultingSublimits: {
    buyerRef: string;
    expectedAdvance: number;
    resultingSublimit: number;
    availableCreditAmount: number;
    expectedAdvancePercentage: number;
    buyerName: string;
  }[];
  conversionRates: { [key: string]: number };
  advanceRate: number;
};

export type APIBatchData = {
  transactionId: number;
  clientCompanyUuid: string;
  batchNumber: number;
  postedDate: string;
  approvedStatus: string;
  invoiceCount: number;
  wireAmount: number;
  inputAmount: number;
  arAmount: number;
  advanceRate: number;
  reserveEscrow: number;
  expensesDeducted: number;
  advanceAmount: number;
  reservesEarned: number;
  reservesHeld: number;
  payeeBankId: number;
  payeeBankAccountNumber: string;
  payeeBankName: string;
  payeeBankAbaNumber: string;
  payeeBankSwiftCode: Nullable<string>;
  payeeBankCurrency: Nullable<string>;
  transactionStatus: string;
  status: number;
  countOfFinanceRequests: number;
};

export type APIBatchDataInvoice = {
  clientCompanyUuid: number;
  clientName: string;
  batchNumber: number;
  transactionId: number;
  postedDate: string;
  invoiceKey: number;
  invoiceNumber: string;
  clientRefCode: string;
  buyerName: string;
  invoiceDate: string;
  dueDate: string;
  purchaseDate: string;
  invoiceAmount: number;
  advanceRate: number;
  advanceAmount: number;
  reserveEscrow: number;
  financeStatus: string;
  otherBatchNumber: Nullable<string>;
  invoiceCount: number;
};

export type APIGetCompletedFinanceRequestDetail = {
  flowData: APIGetFinanceRequestDetail & {
    assets: APIAsset[];
  };
  batchNumber: string;
  batchData: APIBatchData;
  batchDataInvoices: APIBatchDataInvoice[];
};

export type APIGetFinanceRequestDetail = {
  id: number;
  invoiceCount: number;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
  status: 'draft' | 'completed' | 'needs_attention' | 'processing';
  beneficiaryBankAccount: string | null;
  transferType: string | null;
  releaseReserves: boolean;
  releaseReservesAmount: number;
  additionalInformation: string | null;
  reviewStatistics: APIFinanceRequestReviewStatistics | null;
  totalAmount: number;
  isSigned: boolean;
  signDate: string | null;
  signIp: string | null;
  company: number;
  user: number;
  signer: number | null;
  selectedSigner: number | null;
  completedData: Nullable<{
    batchNumber: string;
    batchData: APIBatchData;
    batchDataInvoices: APIBatchDataInvoice[];
  }>;
  batchNumber?: string;
};

export type APIGetFinanceRequestStatistics = {
  invoiceCount: number;
  invoiceAmountSum: number;
  uniqueInvoiceCurrencies: string[];
  conversionRates: { [key: string]: number };
  creditLineSize: APIMoneyField;
  availableCredit: APIMoneyField;
  advanceRate: number;
  expenses: APIMoneyField;
  banks: ApprovedBankAccount[];
  cashReserves: APIMoneyField;
  reserveEscrow: APIMoneyField;
  buyers: APIBuyerDetails[];
};

export type APIBuyerDetails = {
  clientName: string;
  clientCompanyUuid: string;
  buyerName: string;
  clientRefNo: string;
};

export type APIFinanceRequestInvoice = {
  id: number;
  assets: APIAsset[];
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
  status: 'incomplete' | 'uploaded' | 'missing_docs';
  buyerRef: string;
  number: string;
  currency: string;
  amount: number;
  date: string;
  dueDate: string;
  purchaseOrder: string | null;
  description: string | null;
  supportingDocumentsAreOptional: boolean;
  invoiceFundingRequestFlow: number;
  creationType: 'bulk_spreadsheet' | 'manual' | 'bulk_ocr';
};

export type APIGetFinanceRequestInvoices = {
  count: number;
  next: string | null;
  previous: string | null;
  results: APIFinanceRequestInvoice[];
};

export type APIGetInvoiceFields = {
  buyerRef: string;
  number: string;
  currency: string;
  amount: number;
  date: string;
  dueDate: string;
  purchaseOrder: string | null;
  error?: boolean;
  errorDetail?: UploadInvoiceErrorDetails;
};

export type APIGetCompletedFinanceRequestInvoices = {
  pagination: SkipLimitPagination;
  financeRequestDetails: APIBatchDataInvoice[];
};

export type RevolvingCreditLineClient = {
  id: string;
  client: {
    id: string;
    name: string;
  };
  limit: string;
  totalDebt: string;
  totalInterest: string;
  status: string;
};

export type RevolvingCreditLineClientInfo = {
  id: string;
  name: string;
};

export type RevolvingCreditLineMovement = {
  id: string;
  type: string;
  amount: string;
  currency: string;
  creditLine: string;
  totalDebtInitial: string;
  totalDebtFinal: string;
  createdAt: string;
  status: string;
};

export type RevolvingCreditLinePayment = {
  id: string;
  client: RevolvingCreditLineClientInfo;
  amount: string;
  currency: string;
  updatedAt: string;
  status: string;
};

export type RevolvingCreditLineDraw = {
  id: string;
  client: RevolvingCreditLineClientInfo;
  createdAt: string;
  amount: string;
  currency: string;
  totalPaid: string;
  notes: string;
  status: string;
  dueDate: string | null;
};

export type RevolvingCreditLinePendingCharges = {
  fullAmount: number;
  interestAmount: number;
  capitalizationAmount: number;
};

export type RevolvingCreditLineClientDetail = {
  id: string;
  limit: string;
  // Present on factoring lines (product_type === 'factoring'); omitted on revolving lines.
  productType?: string;
  collateral?: string;
  availability?: string;
  availableToDraw?: string;
  termDays: number;
  minDrawAmount: string;
  dailyInterestPct: string;
  drawFeePct: string;
  penaltyFee: string;
  approvedLimitAmount: string;
  totalDebt: string;
  totalInterest: string;
  status: string;
  currency: string;
  movements: RevolvingCreditLineMovement[];
  payments: RevolvingCreditLinePayment[];
  draws: RevolvingCreditLineDraw[];
  monthlyCharges: {
    id: string;
    client: {
      id: string;
      name: string;
    };
    amount: number | null;
    currency: string;
    dueDate: string | null;
    status: string | null;
    month: string | null;
    totalInterest: string;
    totalFee: string;
    adjustment: string;
  }[];
  lastMonthlyCharge: {
    amount: number | null;
    currency: string;
    dueDate: string | null;
    status: string | null;
  };
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
  client: {
    id: string;
    name: string;
    provider: string;
    providerClientId: string;
    accountProvider: string;
    accountId: string;
  };
};
